//#region IMPORTS
import { useState, useEffect, useRef } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { MainComboboxOption } from "./MainComboBoxOption";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  onCreateNewClickHandler?: () => void;
  onChangeHandler: (value: string) => void;
  onSearchHandler: (value: string) => void;
  filteredItems: string[];
  defaultItem?: string;
  placeHolder?: string;
  className?: string;
  showError?: boolean;
  disabled?: boolean;
  hide?: boolean;
}
//#endregion

export default function MainFormComboBoxServerQuery({
  onCreateNewClickHandler,
  onChangeHandler,
  onSearchHandler,
  filteredItems = [],
  placeHolder = "search items",
  defaultItem = "",
  className,
  showError,
  disabled = false,
  hide = false,
}: Props) {
  //#region SETUP
  const DEBOUNCE_DELAY = 300;
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [query, setQuery] = useState(defaultItem ?? "");
  //#endregion

  //#region LOGIC
  // Sync query when external defaultItem changes
  useEffect(() => {
    setQuery(defaultItem ?? "");
  }, [defaultItem]);

  const onSelectHandler = (value: string) => {
    setQuery(value);
    onSearchHandler(value);
    onChangeHandler(value);
  };

  const onSearchClickHandler = (searchValue: string) => {
    if (searchValue === "") {
      onChangeHandler("");
      onSearchHandler("");
    } else {
      onSearchHandler(searchValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      onSearchClickHandler(value);
    }, DEBOUNCE_DELAY);
  };
  //#endregion

  //#region RENDER
  if (hide) return null;

  const placeholderText = disabled ? "" : placeHolder;

  return (
    <Combobox
      as="div"
      className={classNames(className, "grow")}
      value={query}
      onChange={onSelectHandler}
      disabled={disabled}
    >
      <div className="relative">
        <Combobox.Input
          className="block w-full rounded-md px-3 py-1.5 border border-gray-300 text-gray-900 bg-white focus:border-gray-500 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6 placeholder:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-100"
          value={query}
          onChange={handleInputChange}
          displayValue={(value) => String(value)}
          placeholder={placeholderText}
        />

        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none hover:cursor-pointer disabled:cursor-not-allowed">
          <ChevronUpDownIcon
            className={classNames(
              showError
                ? "font-bold text-red-400"
                : "font-medium text-gray-400",
              "h-5 w-5"
            )}
          />
        </Combobox.Button>

        <Transition
          enter="transition duration-200 ease-out"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition duration-150 ease-in"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Combobox.Options className="absolute z-[200] mt-1 max-h-48 w-full overflow-auto rounded-lg bg-gray-100 p-2 text-sm shadow-md scrollbar scrollbar-track-gray-100 scrollbar-thumb-rounded-full scrollbar-thumb-gray-400">
            {filteredItems.length === 0 && (
              <div className="p-2 text-gray-500 text-sm select-none">
                No results found
              </div>
            )}

            {filteredItems.map((item, i) => (
              <MainComboboxOption key={i} value={item} text={item} />
            ))}

            {onCreateNewClickHandler ? (
              <div
                className="sticky bottom-0 bg-gray-200 mt-2 p-2 rounded-md cursor-pointer hover:bg-gray-300 select-none"
                onClick={onCreateNewClickHandler}
              >
                + Create new
              </div>
            ) : null}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
  //#endregion
}
